/**
 * The consolidation picker (TRADE-CONS U4 step 3, Pedro 2026-10-29: "Rebalance to now shows
 * Model, Portfolio and Consolidation"). Same look as `ModelPicker`: a search box, the
 * consolidations with a radio (each naming its agreement, its portfolios on hover), and an apply
 * button + Cancel. Opened by the caller, anchored to `target`.
 */
import { useEffect, useState } from 'react';
import { Button, Group, Popover, Radio, ScrollArea, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { portfolioConsolidationsApi, type Consolidation } from '../api/portfolioConsolidations';

export function ConsolidationPicker({ opened, onClose, onChoose, target, intro, action, busy }: {
  opened: boolean; onClose: () => void; onChoose: (c: Consolidation) => void;
  target: React.ReactNode; intro?: string; action: string; busy?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  useEffect(() => { if (opened) { setChosen(null); setSearch(''); } }, [opened]);
  const q = useQuery({ queryKey: ['portfolio-consolidations'], queryFn: () => portfolioConsolidationsApi.list(), enabled: opened });
  const t = search.trim().toLowerCase();
  const all = q.data?.items ?? [];
  const items = all.filter((c) => !t || c.name.toLowerCase().includes(t) || c.agreement_label.toLowerCase().includes(t));
  const pick = all.find((c) => c.id === chosen);
  return (
    <Popover width={340} position="bottom-start" shadow="md" withArrow opened={opened}
      onChange={(o) => { if (!o) onClose(); }} trapFocus>
      <Popover.Target>{target}</Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          {intro && <Text size="xs" c="dimmed">{intro}</Text>}
          <TextInput size="xs" placeholder="Consolidation or agreement" value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
          <ScrollArea.Autosize mah={300}>
            <Stack gap={4}>
              {items.length === 0 && <Text size="xs" c="dimmed">{q.isLoading ? 'Loading…' : 'No consolidation (create one in Portfolios → Assign to… → Attach to Consolidations)'}</Text>}
              {items.map((c) => (
                <Tooltip key={c.id} withArrow multiline w={300} label={c.members.map((m) => m.label).join(' · ')}>
                  <Radio size="xs" label={`${c.name} · ${c.agreement_label} (${c.members.length} portfolios)`}
                    checked={chosen === c.id} onChange={() => setChosen(c.id)} />
                </Tooltip>
              ))}
            </Stack>
          </ScrollArea.Autosize>
          <Text size="xs" c="dimmed">{pick ? pick.name : 'Choose one'}</Text>
          <Group gap="xs" justify="flex-end">
            <Button size="xs" disabled={!pick || !!busy} onClick={() => { if (pick) { onChoose(pick); onClose(); } }}>{action}</Button>
            <Button size="xs" variant="default" onClick={onClose}>Cancel</Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
